import { parseColor, readableTextOn, visibleOn } from "../engine/contrast";
import {
  MINUTES_PER_DAY,
  durationMinutes,
  formatDuration,
  formatLongDate,
  formatMinutes,
  minutesOfDay
} from "../engine/time";
import {
  type CreateDraft,
  type CreateSource,
  workShiftDraftEnd
} from "./create-draft";
import type { AppointmentFormValues } from "./form-values";
import { privateName } from "./private-name";
import type {
  AppointmentEvent,
  AppointmentTypeId,
  ClinicId,
  PatientId,
  ProviderId
} from "./types";

/**
 * "I know what this is for, now pick when" — the old scheduler's one affordance for it,
 * kept in V3 (ENG-2032, Chen: *"I also want this functionality existing in v3 … and
 * where ever we use it (like in rescheduling)"*).
 *
 * Two subjects, one mechanism: a patient sent over from their chart, or an appointment
 * being replaced. The old app had two near-identical modes with two floating panels and
 * two page borders (`patientCreateAppointmentMode` and `rescheduling` in
 * `scheduler-slice.ts`); this is the same behaviour with one state and one panel
 * (ENG-2208 brought the page frame and the floating panel back, refined).
 *
 * A name is carried twice because the panel renders under privacy mode: `label` is what the
 * form is seeded with (a real field value), `privateLabel` is what the panel shows while
 * privacy is on.
 */
export type SchedulingFor =
  | {
      kind: "patient";
      patient: { id: PatientId; label: string };
      privateLabel: string;
      clinicLabel: string | null;
      /** The chart to put the user back in when the mode ends. */
      returnToPath: string | null;
      /** A type the chart suggested (the visit drawer's follow-up); fills a draft that names none. */
      appointmentTypeId?: number | null;
    }
  | {
      kind: "reschedule";
      appointmentId: number;
      who: string;
      privateLabel: string;
      /** The slot being replaced, already formatted — "Thu, Aug 13 at 2:15pm". */
      fromLabel: string;
      /**
       * The source's length. A replacement keeps it whatever the clicked cell's size,
       * which is the old mode's rule (`use-rescheduling-appointment.hook.ts:59-110`).
       */
      durationMinutes: number;
      /**
       * The source's type and clinic, so the panel can frame the page in the type's colour
       * and say "Hearing test · 45m · Main Port" (ENG-2208). Optional: a mode started by an
       * older caller still works, it just frames in the fallback colour.
       */
      typeId?: AppointmentTypeId | null;
      clinicId?: ClinicId | null;
      /** The new slot, once one has been clicked; `null` until then. */
      pick: ReschedulePick | null;
      returnToPath: null;
    };

/** What starting a reschedule needs to know, read off the appointment being replaced. */
export type RescheduleRequest = Pick<
  Extract<SchedulingFor, { kind: "reschedule" }>,
  | "appointmentId"
  | "who"
  | "privateLabel"
  | "fromLabel"
  | "durationMinutes"
  | "typeId"
  | "clinicId"
>;

/**
 * Everything the panel and the replacement's seed need from the source appointment, read
 * once at the three entry points (the preview's Reschedule, the grid's R, the Day Sheet's
 * status menu) rather than re-derived from a fetch the panel would otherwise have to make.
 *
 * `fromLabel` is deliberately the same string the form's own title uses
 * (`domain/reschedule.ts`), so the panel and the sheet name the same slot the same way.
 */
export function rescheduleRequest(event: AppointmentEvent): RescheduleRequest {
  return {
    appointmentId: event.appointmentId,
    who: event.title,
    privateLabel: privateName(event),
    fromLabel: `${formatLongDate(event.start)}, ${formatMinutes(minutesOfDay(event.start))}`,
    durationMinutes: durationMinutes(event.start, event.end),
    typeId: event.typeId,
    clinicId: event.clinicId
  };
}

/** A slot picked for a replacement booking. The source is read separately by the form. */
export interface ReschedulePick {
  date: string;
  startMinutes: number;
  endMinutes: number;
  providerId: ProviderId | null;
  clinicId: ClinicId | null;
}

/**
 * The picked slot, written over the seed read from the source appointment.
 *
 * Only the four things a slot actually says. Everything else about the replacement —
 * type, patient, notes, and the `rescheduled_appointment_id` that makes it a reschedule
 * at all — comes from `rescheduleSeed`, which this deliberately does not touch. A column
 * with no provider or clinic of its own (a date column in a week view) says nothing about
 * either, so the source's keep.
 */
export const applyReschedulePick = (
  values: AppointmentFormValues,
  pick: ReschedulePick | null
): AppointmentFormValues =>
  pick
    ? {
        ...values,
        date: pick.date,
        startMinutes: pick.startMinutes,
        endMinutes: pick.endMinutes,
        providerId: pick.providerId ?? values.providerId,
        clinicId: pick.clinicId ?? values.clinicId
      }
    : values;

/** A cell the user clicked or dragged, before anything has decided what it means. */
export interface SlotPick {
  date: string;
  start: number;
  end: number;
  providerId: ProviderId | null;
  clinicId: ClinicId | null;
  source: CreateSource;
}

export type SlotIntent =
  | { type: "create"; draft: CreateDraft }
  | { type: "reschedule"; pick: ReschedulePick };

/**
 * What a slot click means right now — the single answer, so the grid asks rather than
 * decides.
 *
 * Three meanings share one gesture: add a work shift, start a booking (for nobody in
 * particular, or for the mode's patient), and re-time an appointment being replaced.
 * Growing that into a chain of `if`s at the dispatch site is how the old app ended up
 * with its precedence written in three files; here the precedence is one ordered function
 * and a test.
 *
 * The order is the old app's (`scheduling.component.tsx:230-259`): rescheduling beats
 * patient-create beats work shifts. Entering either mode leaves the other, so the order
 * is a guarantee rather than a tie-break — but it is stated anyway, because a future
 * fourth mode should have to argue with it.
 */
export function slotIntent(
  pick: SlotPick,
  ctx: { workShiftMode: boolean; schedulingFor: SchedulingFor | null }
): SlotIntent {
  const mode = ctx.schedulingFor;
  if (mode?.kind === "reschedule")
    return {
      type: "reschedule",
      pick: {
        date: pick.date,
        startMinutes: pick.start,
        endMinutes: Math.min(
          MINUTES_PER_DAY,
          pick.start + mode.durationMinutes
        ),
        providerId: pick.providerId,
        clinicId: pick.clinicId
      }
    };

  const draft: CreateDraft = {
    date: pick.date,
    start: pick.start,
    end: pick.end,
    providerId: pick.providerId,
    clinicId: pick.clinicId,
    source: pick.source
  };

  if (ctx.workShiftMode)
    return {
      type: "create",
      draft: {
        ...draft,
        end: workShiftDraftEnd(pick.start, pick.end, pick.source),
        kind: "workShift"
      }
    };

  if (mode)
    return {
      type: "create",
      draft: { ...draft, patient: mode.patient, ...suggestedType(mode) }
    };
  return { type: "create", draft };
}

/** The subject's suggested type, as a draft field — nothing at all when there is none. */
export function suggestedType(mode: SchedulingFor | null): {
  appointmentTypeId?: number;
} {
  return mode?.kind === "patient" && mode.appointmentTypeId
    ? { appointmentTypeId: mode.appointmentTypeId }
    : {};
}

/** The colours "scheduling for" paints the page and its panel in (ENG-2208). */
export interface SchedulingForColors {
  /**
   * The 10px page frame. The type colour, stepped toward black only as far as it takes to
   * reach 3:1 on white (WCAG 1.4.11) — the grid cards' white-card border rule
   * (`visibleOn`) — so a pale type such as #F8F0FB (1.1:1) still frames the page.
   */
  frame: string;
  /** The panel's header band: the type colour itself, as a filled card keeps its fill. */
  band: string;
  /** Text and icon on the band: whichever of white and black MEASURES better on it. */
  ink: string;
}

/**
 * The colours the page is framed in while a mode is on — the old scheduler's 10px border
 * (`scheduling.component.tsx:230-253`), kept because it is how a front desk sees, from
 * across the room, that the next click is not an ordinary one (ENG-2208).
 *
 * A reschedule wears the source's TYPE colour, so the frame says which appointment is being
 * moved; a patient sent over from their chart wears the link colour, as in the old app. A
 * reschedule whose type colour is unknown (not loaded yet, none) or UNREADABLE falls back to
 * the same: imported data carries named CSS colours, and a colour `parseColor` cannot read
 * can be neither measured nor stepped. The theme's colours are passed in so this stays free
 * of the theme.
 */
export function schedulingForColors(
  mode: SchedulingFor,
  typeColor: string | null | undefined,
  theme: { fallback: string; light: string; dark: string }
): SchedulingForColors {
  const typeRgb = mode.kind === "reschedule" ? parseColor(typeColor) : null;
  const base = typeRgb && typeColor ? typeColor.trim() : theme.fallback;
  const ground = typeRgb ?? parseColor(theme.fallback);
  const light = parseColor(theme.light);
  const dark = parseColor(theme.dark);
  if (!ground || !light || !dark)
    return { frame: base, band: base, ink: theme.light };
  return {
    frame: visibleOn(light, ground, dark) ?? base,
    band: base,
    ink:
      readableTextOn(ground, light, dark) === light ? theme.light : theme.dark
  };
}

/**
 * Which mode this is, as a React key: a new subject remounts the draggable panel, so it
 * starts at its default spot instead of wherever the last mode's panel was dragged. A pick
 * within the same mode keeps the key, and so the spot.
 */
export function schedulingForIdentity(mode: SchedulingFor): string {
  return mode.kind === "patient"
    ? `patient:${mode.patient.id}`
    : `reschedule:${mode.appointmentId}`;
}

/** One chip on the panel: "From …", "To …" or "When …". */
export interface SchedulingForChip {
  label: "From" | "To" | "When";
  /** `null` until a slot has been picked; the panel then draws the dashed hint. */
  text: string | null;
}

/** Everything the floating panel says, in the order it says it. */
export interface SchedulingForPanelText {
  /** The header band: what the mode is. */
  band: "Rescheduling" | "Scheduling for";
  /** The patient, large. */
  name: string;
  /** "Hearing test · 45m · Main Port", or "New appointment · Main Port". */
  summary: string;
  chips: SchedulingForChip[];
}

/** Names the panel cannot read off the mode, resolved from the catalog by the caller. */
export interface SchedulingForPanelNames {
  isPrivateMode: boolean;
  typeName?: string | null;
  clinicName?: string | null;
  /** The provider of the picked slot's column, when it has one. */
  pickProviderName?: string | null;
}

/** What a dashed, not-yet-picked chip says. */
export const PICK_A_TIME_HINT = "Click an open time on the grid";

/**
 * The chart hands the label over, so it can arrive empty: a patient record whose name fields
 * are blank, an entry clicked before the name resolved, or privacy mode with no initials to
 * make. The mode still knows WHO it is for — the id rides with it and the booking is correct —
 * so the panel says something true rather than trailing off into nothing (ENG-2039).
 */
const named = (label: string, fallback: string) => label.trim() || fallback;

const joined = (parts: (string | null | undefined)[]) =>
  parts.filter((p): p is string => Boolean(p?.trim())).join(" · ");

/** "Tuesday, September 15th, 9 AM" — the same shape as `fromLabel`, so From and To match. */
export const pickLabel = (pick: ReschedulePick): string =>
  // `T00:00` so the date parses in the browser's zone; a bare `YYYY-MM-DD` is read as UTC
  // and names the previous day anywhere west of Greenwich.
  `${formatLongDate(`${pick.date}T00:00:00`)}, ${formatMinutes(pick.startMinutes)}`;

/**
 * What the panel says (ENG-2208). The patient mode has no "To": its slot click opens the
 * form straight away and the draft is cleared when the form closes, so the state never
 * holds a picked time the panel could show — its "When" stays the hint.
 */
export function schedulingForPanelText(
  mode: SchedulingFor,
  names: SchedulingForPanelNames
): SchedulingForPanelText {
  if (mode.kind === "patient") {
    return {
      band: "Scheduling for",
      name: named(
        names.isPrivateMode ? mode.privateLabel : mode.patient.label,
        "This patient"
      ),
      summary: joined([
        "New appointment",
        names.clinicName ?? mode.clinicLabel
      ]),
      chips: [{ label: "When", text: null }]
    };
  }
  return {
    band: "Rescheduling",
    name: named(
      names.isPrivateMode ? mode.privateLabel : mode.who,
      "This appointment"
    ),
    summary: joined([
      names.typeName,
      formatDuration(mode.durationMinutes),
      names.clinicName
    ]),
    chips: [
      { label: "From", text: mode.fromLabel },
      {
        label: "To",
        text: mode.pick
          ? joined([pickLabel(mode.pick), names.pickProviderName])
          : null
      }
    ]
  };
}
